import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";

export default function HeathrowfreeDropoffCalculator() {
  const [form, setForm] = useState({
    terminal: "T5",
    journey_type: "DROPOFF",
    planned_minutes: 10,
    vehicle_type: "car",
    blue_badge: false,
    blue_badge_registered: false,
    overheight: false
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const autoSubmitted = useRef(false);
  const changeTimer = useRef(null);

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      terminal: "T5",
      journey_type: "DROPOFF",
      planned_minutes: 10,
      vehicle_type: "car",
      blue_badge: false,
      blue_badge_registered: false,
      overheight: false
    }));
  }, []);

  useEffect(() => {
    if (autoSubmitted.current) return;
    autoSubmitted.current = true;
    submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!autoSubmitted.current) return;
    if (changeTimer.current) clearTimeout(changeTimer.current);
    changeTimer.current = setTimeout(() => {
      submit();
    }, 320);
    return () => {
      if (changeTimer.current) clearTimeout(changeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const clampMinutes = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return 10;
    return Math.min(10080, Math.max(1, num));
  };

  const submit = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...form,
        planned_minutes: clampMinutes(form.planned_minutes)
      };

      if (!payload.blue_badge) payload.blue_badge_registered = false;

      const res = await api.post("/api/heathrow-dropoff/calc", payload);
      setResult(res.data);
    } catch (err) {
      setError("Failed to calculate drop-off options. Please try again.");
    }

    setLoading(false);
  };

  const money = (v) => (v == null ? "—" : `£${Number(v).toFixed(2)}`);

  const optionLabel = (id) => {
    if (id === "DROPOFF_ZONE") return "Terminal drop-off";
    if (id === "SHORT_STAY") return "Short Stay parking";
    if (id === "PARK_RIDE") return "Park & Ride / Long Stay";
    return id || "—";
  };

  const optionTagline = (id) => {
    if (id === "DROPOFF_ZONE") return "Fastest forecourt stop (PCN risk if long stay)";
    if (id === "SHORT_STAY") return "Best for pick-ups / waiting near terminal";
    if (id === "PARK_RIDE") return "Free drop-off + shuttle to terminal";
    return "Compare by price & hassle";
  };

  const renderOptionCard = (opt) => {
    if (!opt) return null;
    const isBest = result?.recommendation?.best === opt.id;

    const linkBlocks = {
      DROPOFF_ZONE: [
        { label: "Pay drop-off charge (£7)", href: "https://heathrowdropoff.apcoa.com/trip/vrn" },
        { label: "Pay or appeal a PCN", href: "https://heathrowdropoffpcnpayments.apcoa.com/pcnonline/index.php" },
        { label: "Blue Badge discount / registration", href: "https://heathrowdropoff.apcoa.com/account/login?returnUrl=%2Fbluebadgetrip%2Fvrn" }
      ],
      PARK_RIDE: [
        { label: "Long Stay / Park & Ride info", href: "https://www.heathrow.com/transport-and-directions/heathrow-parking" }
      ],
      SHORT_STAY: [
        { label: "Short Stay parking prices", href: "https://www.heathrow.com/transport-and-directions/heathrow-parking/heathrow-short-stay-parking" }
      ]
    };

    return (
      <div
        key={opt.id}
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 14,
          marginBottom: 12,
          background: isBest ? "#ecfdf5" : "#fff"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ fontWeight: 800 }}>
            {opt.title}
            {isBest && (
              <span
                style={{
                  display: "inline-block",
                  padding: "2px 8px",
                  borderRadius: 999,
                  fontSize: 12,
                  marginLeft: 8,
                  border: "1px solid #e5e7eb",
                  background: "#ecfdf5",
                  color: "#111827"
                }}
              >
                Recommended
              </span>
            )}
          </div>
          <div style={{ fontWeight: 800 }}>{money(opt.cost)}</div>
        </div>

        {opt.best_for?.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 13, color: "#374151" }}>
            Best for:{" "}
            <span style={{ color: "#111827" }}>
              {opt.best_for.includes("free_dropoff") ? "free / cheapest, " : ""}
              {opt.best_for.includes("pickup_best") ? "pick-ups, " : ""}
              {opt.best_for.includes("fastest") ? "fastest, " : ""}
              {opt.best_for.includes("waiting") ? "waiting, " : ""}
              {opt.best_for.includes("meet_and_greet_style") ? "meet & greet style, " : ""}
            </span>
          </div>
        )}

        {Array.isArray(opt.warnings) && opt.warnings.length > 0 && (
          <div style={{ marginTop: 10, background: "#fff7ed", border: "1px solid #fed7aa", padding: 10, borderRadius: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠️ Important</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              {opt.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        {Array.isArray(opt.notes) && opt.notes.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5, color: "#374151" }}>
              {opt.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </div>
        )}

        {linkBlocks[opt.id] && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Next steps</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              {linkBlocks[opt.id].map((link, i) => (
                <li key={i}>
                  <a href={link.href} target="_blank" rel="noreferrer">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderOptionSection = (opt, title, defaultOpen = false) => {
    if (!opt) return null;
    return (
      <details id={`collapse-${opt.id.toLowerCase().replace(/_/g, "_")}`} className="train-collapsible" open={defaultOpen} style={{ marginTop: 12 }}>
        <summary className="train-summary-bar">{title}</summary>
        <div className="train-collapsible-body">{renderOptionCard(opt)}</div>
      </details>
    );
  };

  const renderMedalHero = () => {
    const palette = {
      GOLD: { bg: "#0b172a", text: "#5eead4", border: "#22d3ee", emoji: "🥇" },
      SILVER: { bg: "#1f2937", text: "#e5e7eb", border: "#cbd5e1", emoji: "🥈" },
      BRONZE: { bg: "#2b1b0f", text: "#fdba74", border: "#fb923c", emoji: "🥉" }
    };

    const options = Array.isArray(result?.options) ? result.options : [];
    const numMinutes = (() => {
      const fromResult = Number(result?.journey?.planned_minutes);
      if (Number.isFinite(fromResult)) return fromResult;
      const fromForm = Number(form.planned_minutes);
      if (Number.isFinite(fromForm)) return fromForm;
      return 0;
    })();

    const byId = options.reduce((acc, opt) => ({ ...acc, [opt.id]: opt }), {});
    const isExempt =
      form.blue_badge ||
      form.blue_badge_registered ||
      ["emergency", "recovery", "military"].includes(String(form.vehicle_type || "").toLowerCase());

    const baseOption = (id) => {
      const raw = byId[id];
      if (!raw) return null;
      const warnings = Array.isArray(raw.warnings) ? [...raw.warnings] : [];
      const infoNotes = [];

      if (id === "DROPOFF_ZONE" && !isExempt && numMinutes > 10) {
        warnings.unshift("Forecourt stay over 10 minutes risks an £80 PCN (reduced to £40 if paid within 14 days).");
      }
      if (id === "PARK_RIDE") {
        warnings.unshift("Free drop-off + shuttle to terminal.");
        if (numMinutes <= 30) infoNotes.push("Keep your Park & Ride stop to 30 minutes to stay free.");
      }
      if (id === "DROPOFF_ZONE" && isExempt) {
        infoNotes.push("♿ Accessibility priority: Blue Badge / exempt vehicles can use the forecourt.");
        infoNotes.push("ℹ️ Blue Badge holders are exempt from standard drop-off time limits at this terminal. Display badge clearly.");
      }
      return {
        ...raw,
        id,
        label: optionLabel(id),
        tagline: optionTagline(id),
        cost: Number.isFinite(Number(raw.cost)) ? Number(raw.cost) : null,
        warnings,
        infoNotes,
        isExempt: id === "DROPOFF_ZONE" && isExempt
      };
    };

    const dropoff = baseOption("DROPOFF_ZONE");
    const park = baseOption("PARK_RIDE");
    const shortStay = baseOption("SHORT_STAY");

    const optsArray = [dropoff, shortStay].filter(Boolean);

    const medalDecision = () => {
      let copy = "Cheapest option overall.";

      const dropoffInvalid = numMinutes > 10 && !isExempt;
      const dropoffUsable = dropoff && !dropoffInvalid;

      if (dropoffUsable && dropoff.cost === 0) {
        const silver = shortStay || park || null;
        const bronze = silver && park && silver.id !== park.id ? park : null;
        copy = "Drop-off is free/exempt based on your inputs.";
        return {
          gold: dropoff,
          silver: silver || dropoff,
          bronze: bronze || dropoff,
          copy,
          hasFree: true
        };
      }

      // Only drop-off vs Short Stay comparison
      const shortCost = shortStay?.cost ?? Number.POSITIVE_INFINITY;
      const dropCost = dropoffUsable ? dropoff?.cost ?? Number.POSITIVE_INFINITY : Number.POSITIVE_INFINITY;

      const useDrop = dropoffUsable && dropCost <= shortCost;
      const gold = useDrop ? dropoff : shortStay || dropoff;
      const silver = useDrop ? shortStay || dropoff : dropoff || shortStay;
      const bronze = null;

      copy = useDrop
        ? "Drop-off wins for your planned duration."
        : "Short Stay wins for your planned duration (drop-off risks PCN after 10 mins).";

      return { gold, silver, bronze, copy, hasFree: false, dropoffInvalid };
    };

    const { gold, silver, bronze, copy: decisionCopy, hasFree, dropoffInvalid } = medalDecision();

    const goldLine =
      gold && gold.cost != null
        ? `${gold.label} – ${money(gold.cost)}`
        : gold
        ? gold.label
        : "We compared drop-off vs Short Stay";

    const dropWins = gold?.id === "DROPOFF_ZONE";

    return (
      <div
        style={{
          borderRadius: 18,
          padding: 18,
          marginBottom: 16,
          color: dropWins ? "#0f172a" : "#fff",
          background: dropWins
            ? "linear-gradient(135deg, #e5e7eb 0%, #cbd5e1 100%)"
            : "radial-gradient(circle at 20% 20%, #1d4ed8 0%, #0f172a 40%, #0b132b 100%)",
          border: dropWins ? "1px solid #d1d5db" : "none",
          boxShadow: dropWins ? "0 6px 16px rgba(15, 23, 42, 0.08)" : "0 10px 25px rgba(0,0,0,0.12)"
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.4, textTransform: "uppercase", opacity: 0.9 }}>
          Drop-off vs Short Stay (2026)
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
          {gold?.id === "DROPOFF_ZONE" ? "Terminal drop-off is best for your stay" : "Short Stay is best for your stay"}
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, marginTop: 6 }}>
          Gold pick: {goldLine}
        </div>
        <div style={{ fontSize: 14, marginTop: 6, opacity: 0.9 }}>
          {gold?.id === "DROPOFF_ZONE"
            ? "Drop-off beats Short Stay for your planned duration."
            : "Short Stay beats drop-off for your planned duration. Drop-off risks a PCN after 10 minutes."}
        </div>
        <div style={{ fontSize: 12, marginTop: 6, opacity: 0.85 }}>
          Need Park &amp; Ride or all options? Use the full calculator: <a href="/heathrow-dropoff-calculator" style={{ color: dropWins ? "#0f172a" : "#e5e7eb", textDecoration: "underline", fontWeight: 700 }}>heathrow-dropoff-calculator</a>.
        </div>
        {decisionCopy && <div style={{ fontSize: 13, marginTop: 6, opacity: 0.85 }}>{decisionCopy}</div>}
        {result?.recommendation?.reason && (
          <div style={{ fontSize: 13, marginTop: 6, opacity: 0.85 }}>{result.recommendation.reason}</div>
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 10, fontSize: 12, fontWeight: 700, flexWrap: "wrap" }}>
          <span>⚖️ Compares drop-off vs Short Stay</span>
          <span>💷 Full comparison shown</span>
          <span>🥇 Gold = best overall</span>
        </div>
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 10
          }}
        >
          {[{ medal: "GOLD", opt: gold }, { medal: "SILVER", opt: silver }]
            .filter(({ opt }) => !!opt)
            .map(({ medal, opt }) => {
            const paletteFor = palette[medal];
            return (
              <div
                key={medal}
                style={{
                  border: `1px solid ${paletteFor.border}`,
                  borderRadius: 14,
                  padding: 14,
                  background: paletteFor.bg,
                  color: paletteFor.text,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  minHeight: 140
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.5, color: "#fff" }}>
                  {paletteFor.emoji} {medal}
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#fff" }}>{opt?.label || "Awaiting option"}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>
                  {opt?.cost != null ? money(opt.cost) : "—"}
                </div>
                <div style={{ fontSize: 12, color: "#e5e7eb", lineHeight: 1.4 }}>{opt?.tagline || "Compare safety, speed, and price."}</div>
                {Array.isArray(opt?.warnings) && opt.warnings[0] && (
                  <div style={{ fontSize: 12, color: paletteFor.text }}>{`⚠️ ${opt.warnings[0]}`}</div>
                )}
                {Array.isArray(opt?.infoNotes) &&
                  opt.infoNotes.map((n, idx) => (
                    <div key={idx} style={{ fontSize: 12, color: "#cbd5e1" }}>
                      {n}
                    </div>
                  ))}
                <div style={{ marginTop: "auto" }}>
                  <button
                    type="button"
                    onClick={() => {
                      const map = {
                        DROPOFF_ZONE: "dropoff_zone",
                        SHORT_STAY: "short_stay",
                        PARK_RIDE: "park_ride"
                      };
                      const key = map[opt?.id];
                      if (!key) return;
                      const el = document.getElementById(`collapse-${key}`);
                      if (el) {
                        el.open = true;
                        requestAnimationFrame(() => {
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                        });
                      }
                    }}
                    style={{
                      marginTop: 8,
                      background: "transparent",
                      border: "1px solid rgba(255,255,255,0.2)",
                      color: "#fff",
                      padding: "6px 10px",
                      borderRadius: 10,
                      cursor: "pointer",
                      fontWeight: 700,
                    fontSize: 12
                  }}
                >
                  View details ▸
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    );
  };

  const winReasons = (() => {
    if (!result) return [];
    const reasons = [];
    const map = {};
    (Array.isArray(result.options) ? result.options : []).forEach((o) => (map[o.id] = o));
    const drop = map["DROPOFF_ZONE"] || null;
    const shortStay = map["SHORT_STAY"] || null;
    const mins = clampMinutes(form.planned_minutes);

    if (drop && shortStay && drop.cost != null && shortStay.cost != null) {
      reasons.push(`Drop-off ${money(drop.cost)} vs Short Stay ${money(shortStay.cost)} for your stay.`);
    }
    if (mins > 10 && !(form.blue_badge || form.blue_badge_registered || ["emergency", "recovery", "military"].includes(String(form.vehicle_type || "").toLowerCase()))) {
      reasons.push("Forecourt over 10 minutes risks an £80 PCN (or £40 if paid in 14 days).");
    }
    if (mins <= 10) {
      reasons.push("Under 10 minutes: forecourt is the fastest stop.");
    }
    if (form.blue_badge) {
      reasons.push("Blue Badge may reduce forecourt cost and remove time limit when registered.");
    }
    return reasons;
  })();

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>Heathrow Drop-off vs Short Stay (Avoid £80 PCN)</title>
        <meta
          name="description"
          content="Compare forecourt drop-off vs Short Stay for your planned time. Highlights PCN risk after 10 minutes and Blue Badge/exemption handling."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/heathrow-dropoff-vs-shortstay" />
        <meta property="og:title" content="Heathrow Drop-off vs Short Stay (Avoid £80 PCN)" />
        <meta
          property="og:description"
          content="See whether forecourt drop-off or Short Stay wins for your planned duration. Includes PCN risk, Blue Badge, and exemption handling."
        />
      </Helmet>

      <LiveStrip variant="heathrow" />

      <div className="parking-header">
        <h1>Heathrow Drop-off vs Short Stay</h1>
        <p style={{ fontSize: 15 }}>
          Find whether forecourt drop-off or <strong>Short Stay</strong> is best for your time at the terminal. Highlights PCN risk after 10 minutes and Blue Badge handling.
        </p>
        <p style={{ fontSize: 14, color: "#374151", marginBottom: 0 }}>
          Need all options including Park &amp; Ride? <a href="/heathrow-dropoff-calculator">Open the full calculator</a>.
        </p>
      </div>

      <div className="parking-form">
        <div className="form-grid-compact">
          <div className="form-group">
            <label>Terminal</label>
            <select
              className="parking-input"
              value={form.terminal}
              onChange={(e) => setForm({ ...form, terminal: e.target.value })}
            >
              <option value="T2">Terminal 2</option>
              <option value="T3">Terminal 3</option>
              <option value="T4">Terminal 4</option>
              <option value="T5">Terminal 5</option>
            </select>
          </div>

          <div className="form-group">
            <label>Journey type</label>
            <select
              className="parking-input"
              value={form.journey_type}
              onChange={(e) => setForm({ ...form, journey_type: e.target.value })}
            >
              <option value="DROPOFF">Drop-off</option>
              <option value="PICKUP">Pick-up</option>
            </select>
            <div className="compact-hint">Pick-ups are usually from Short Stay or Park &amp; Ride.</div>
          </div>

          <div className="form-group">
            <label>Vehicle type</label>
            <select
              className="parking-input"
              value={form.vehicle_type}
              onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
            >
              <option value="car">Car</option>
              <option value="taxi_phv">Taxi / Private Hire</option>
              <option value="coach_bus">Bus / Coach</option>
              <option value="motorcycle">Motorcycle</option>
              <option value="recovery">Recovery vehicle</option>
              <option value="emergency">Emergency vehicle</option>
              <option value="military">Military vehicle</option>
            </select>
          </div>

          <div className="toggle-stack">
            <div className="toggle-grid">
              <label className={`toggle-pill ${form.blue_badge ? "active" : ""}`}>
                <input
                  type="checkbox"
                  checked={form.blue_badge}
                  onChange={(e) => setForm({ ...form, blue_badge: e.target.checked, blue_badge_registered: false })}
                />
                <span>Blue Badge holder</span>
              </label>

              {form.blue_badge && (
                <label className={`toggle-pill ${form.blue_badge_registered ? "active" : ""}`}>
                  <input
                    type="checkbox"
                    checked={form.blue_badge_registered}
                    onChange={(e) => setForm({ ...form, blue_badge_registered: e.target.checked })}
                  />
                  <span>Trip registered / approved</span>
                </label>
              )}

              <label className={`toggle-pill ${form.overheight ? "active" : ""}`}>
                <input
                  type="checkbox"
                  checked={form.overheight}
                  onChange={(e) => setForm({ ...form, overheight: e.target.checked })}
                />
                <span>Overheight vehicle</span>
              </label>
            </div>
          </div>
        </div>

        <div className="minutes-card" style={{ marginTop: 12 }}>
          <div className="minutes-label-row">
            <label style={{ margin: 0 }} htmlFor="planned_minutes">Duration of stay (mins)</label>
            <span className="minutes-chip">Time</span>
          </div>
          <div className="minutes-input-row">
            <input
              type="number"
              className="parking-input"
              min="1"
              max="10080"
              step="1"
              id="planned_minutes"
              value={form.planned_minutes}
              onChange={(e) =>
                setForm({
                  ...form,
                  planned_minutes: e.target.value === "" ? "" : clampMinutes(e.target.value)
                })
              }
              onBlur={() => {
                setForm({ ...form, planned_minutes: clampMinutes(form.planned_minutes) });
              }}
            />
            <div className="minutes-nudge">
              <button
                type="button"
                onClick={() => setForm({ ...form, planned_minutes: clampMinutes((Number(form.planned_minutes) || 0) - 1) })}
              >
                −
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, planned_minutes: clampMinutes((Number(form.planned_minutes) || 0) + 1) })}
              >
                +
              </button>
            </div>
          </div>
        </div>

        <button onClick={submit} className="parking-btn">
          {loading ? "Comparing…" : "Compare drop-off vs Short Stay"}
        </button>

        {error && <p style={{ color: "red", marginTop: 10 }}>{error}</p>}
      </div>

      {result && (
        <div className="parking-results">
          {renderMedalHero()}

        <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Why this option wins</div>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6, color: "#374151" }}>
            {winReasons.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
            {winReasons.length === 0 && <li>We ranked options by price, PCN risk, and convenience for your stay.</li>}
          </ul>
        </div>

        {(() => {
          const map = {};
          (Array.isArray(result.options) ? result.options : []).forEach((o) => {
            map[o.id] = o;
          });
          const drop = map["DROPOFF_ZONE"] || null;
          const park = map["PARK_RIDE"] || null;
          const shortStay = map["SHORT_STAY"] || null;

          return (
            <>
              {renderOptionSection(drop, "Terminal drop-off", false)}
              {renderOptionSection(shortStay, "Short Stay parking", false)}
            </>
          );
        })()}

          <details className="train-collapsible">
            <summary className="train-summary-bar">ℹ️ What this tool checks</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>Forecourt £7 charge vs free Park &amp; Ride vs Short Stay.</li>
                <li>Blue Badge exemptions (forecourt discount + no 10-minute rule).</li>
                <li>Automatic vehicle exemptions (some emergency/recovery/military types).</li>
                <li>PCN risk after 10 minutes on the forecourt.</li>
              </ul>
            </div>
          </details>

          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">🆘 Need help with drop-off or parking?</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>📞 Customer services: <a href="tel:03332007459">0333 200 7459</a></li>
                <li>📧 Refunds: <a href="mailto:refunds.heathrowdropoff@apcoa.com">refunds.heathrowdropoff@apcoa.com</a></li>
                <li>💬 Live webchat (official): <a href="https://heathrowdropoff.apcoa.com/help" target="_blank" rel="noreferrer">Start chat</a></li>
                <li>⏰ Hours: Mon–Fri 08:00–20:00 | Weekends 08:00–17:00</li>
              </ul>
            </div>
          </details>

          <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
            Last updated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
          </div>

          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
            <strong>Plan your Heathrow journey:</strong>
            <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
              <li>🚖 <a href="/heathrow-fare-compare">Compare taxi fares</a></li>
              <li>🚆 <a href="/train-vs-taxi">Train vs taxi</a></li>
              <li>🅿️ <a href="/heathrow-parking-vs-taxi">Parking vs taxi</a></li>
              <li>✈️ <a href="/flight-checker">Check your flight</a></li>
              <li>🕒 <a href="/leave-for-heathrow">When to leave for Heathrow</a></li>
              <li>🧭 <a href="/terminalFinder">Find your terminal</a></li>
              <li>🧳 <a href="/arrival-assistant">Arrival assistant</a></li>
            </ul>
          </div>
        </div>
      )}

      <div style={{ marginTop: 28 }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
